import React, {useEffect, useRef} from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from "react-i18next";

import { useDispatch } from "react-redux";
import {setDocumentToPrev, setLoadError} from '../../store/reducers/documents';

import clsx from 'clsx';

import {
    UnselectedIcon,
    SelectedIcon,
    SavedIcon, SaveIcon2, FinalAnnotationIcon,
} from '../../components/icons/icons';

import Formatter from "../../util/formatter";

import config from "../../app/config";
import moment from 'moment';
import Tooltip from "@mui/material/Tooltip";

const getStyleBySize = (size, theme) => ({
    thumbnail: {
        display: "inline-block",
        // margin: !size.ignoreMargin ? "5px 2px 15px 2px" : 0,
        margin: !size.ignoreMargin ? "7px 2px 2px 2px" : 0,
        // FIXME: depends on skin color - should be set in global variables
        backgroundColor: "#252525",
        padding: !size.ignoreMargin ? "8px" : 0,
        // cursor: "pointer",
    },
    thumbnailLabelCount: {
        paddingTop: 2,
        opacity: .75,
        fontSize: "70%",
        lineHeight: "normal",
        float: "right",
    },
    thumbnailLabel: {
        maxWidth: size.width,
        fontSize: "80%",
        overflow: "hidden", whiteSpace: "normal", textOverflow: "ellipsis",
        transition: "all .1s",
        lineHeight: "normal",
        cursor: "pointer"
        // textAlign: "right",
    },
    selectBox: {
        display: "inline",
        cursor: "pointer",
    },
    selectBoxIcon: {
        width: 12,
        height: 12,
        marginRight: 5,
        float: "left",
        marginTop: 1,
    },
    labelProcedureName: {
        fontSize: "90%",
        display: "inline-block",
        // maxWidth: "30%",
        // maxWidth: "30px",
        maxWidth: size.width - 70,
        position: "relative",
        top: "2px",
        overflow: "hidden",
        textOverflow: "ellipsis"
    },
    labelLaterality: {
        marginLeft: 5,
    },
    thumbnailImageContainer: {
        position: "relative",
        width: size.width,
        height: size.height,
        marginBottom: !size.ignoreMargin ? "5px" : 0,
        transition: "all .1s",
        cursor: "pointer",
    },
    thumbnailImageBackground: {
        width: size.width,
        height: size.height,
        position: "absolute",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all .1s",
        backgroundColor: "#000",
        boxShadow: !size.ignoreMargin ? theme.shadows[7] : "none",
        "&.os": {
            borderBottom: "3px solid "+ config.thumbnailsBorder?.os
        },
        "&.od": {
            borderBottom: "3px solid "+ config.thumbnailsBorder?.od
        },
        "&:hover": {
            marginLeft: size.moveLeft,
            marginTop: size.moveTop,
            width: size.zoomWidth,
            height: size.zoomHeight,
            zIndex: 10,
            "& $thumbnailImage": {
                width: size.zoomWidth,
                height: size.zoomHeight,
                maxWidth: size.zoomWidth,
                maxHeight: size.zoomHeight,
            },
        },
    },
    thumbnailImage: {
        position: "absolute",
        width: size.width,
        height: size.width,
        objectFit: "contain",
        maxWidth: size.width,
        maxHeight: size.height - 3,
        transition: "all .1s",
        // FIXME: depends on skin color - should be set in global variables
        // backgroundColor: "#323232",
    },
    thumbnailIcon: {
        position: "absolute",
        width: 26,
        height: 26,
        top: 5,
        right: 5,
        backgroundColor: "#222",
        zIndex: 2,
        borderRadius: 5,
        opacity: .75,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&.old-annotations": {
            backgroundColor: "#e70e66 !important",
        },
    },

    thumbnailIconOldCopy: {
        height: "100%",
        width: "100%",
        left: 0,
        backgroundColor: "#000000",
        top: 0
    },
    thumbnailIconFinal: {
        backgroundColor: "#39b31d",
    },
    thumbnailIconSVG: {
        position: "relative",
        // left: 4,
        // top: 4,
        width: 20,
        color: "#00CC33" ,
        height: 20,
    },
    thumbActive: {
        border: "1px solid #9999ff"
    }
});

const useStylesSmall = makeStyles((theme) => (getStyleBySize(config.modules.thumbnail.size.small, theme)));
const useStylesBig = makeStyles((theme) => (getStyleBySize(config.modules.thumbnail.size.big, theme)));
const useStylesPen = makeStyles((theme) => (getStyleBySize(config.modules.thumbnail.size.pen, theme)));

function Thumbnail(props) {
    const classes = {
        small: useStylesSmall(),
        big: useStylesBig(),
        pen: useStylesPen(),
    };
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const thumbTimeoutRef = useRef(null);

    const [loading, setLoading] = React.useState(true);
    const [isOldAnnotation, setIsOldAnnotation] = React.useState(false);

    const openDocument = () => {
        if (props.onOpenDocument) {
            props.onOpenDocument();
        }
    };
    const documentLoadError = () => {
        setLoading(false);
        if (props.onImageLoadError) {
            props.onImageLoadError();
        } else {
            dispatch(setLoadError(props.document));
        }
    };

    const pointerEnter = (e) => {
        if(!testIsOldAnnotation()) {
            thumbTimeoutRef.current = setTimeout(() => {
                dispatch(setDocumentToPrev(props.document))
            }, (config.modules.thumbnailPrev?.time || 700));
        }
    }

    const pointerLeave = (e) => {
        clearTimeoutPrev()
    }

    const clearTimeoutPrev = (e) => {
        clearTimeout(thumbTimeoutRef.current);
    }

    useEffect(() => {
        return () => {
            clearTimeoutPrev();
        }
    }, []);
    
    useEffect(()=> {
        setIsOldAnnotation(testIsOldAnnotation())
    },[props.document]);

    const testIsOldAnnotation = () => {
        return props.document.metadata.saved && moment(props.document.startDate).isBefore(moment(config.annotationsLegacyCompatibilityDate))
    }

    const getTooltipText = () => {
        return isOldAnnotation? "Legacy annotations cannot be viewed on the Image Preview. Please open the document in order to see the annotation." : ""
    }

    const imageSize = props.size ? props.size : "small";

    return (
        <Tooltip title={getTooltipText()} >
        <div className={classes[imageSize].thumbnail}>

            <div onClick={openDocument}
                 className={classes[imageSize].thumbnailImageContainer}
                 onPointerEnter={pointerEnter}
                 onPointerLeave={pointerLeave}
            >
                {loading &&
                    <div className={classes[imageSize].thumbnailImageBackground} style={{zIndex: 1}}>
                        <img
                            className={classes[imageSize].thumbnailImage}
                            alt="" src={'/image/lob.gif'}/>
                    </div>
                }

                <div className={clsx(classes[imageSize].thumbnailImageBackground, props.document.metadata.laterality === 'L' && 'os', props.document.metadata.laterality === 'R' && 'od')}>
                    {props.document.metadata.saved && !props.document.metadata.annotationFinal &&
                            <div className={clsx(classes[imageSize].thumbnailIcon, isOldAnnotation && 'old-annotations')}>
                                <SaveIcon2 className={classes[imageSize].thumbnailIconSVG}/>
                            </div>
                    }


                    {props.document.metadata.saved && props.document.metadata.annotationFinal &&
                            <div className={clsx(classes[imageSize].thumbnailIcon, isOldAnnotation && 'old-annotations')}>
                                <FinalAnnotationIcon className={classes[imageSize].thumbnailIconSVG}/>
                            </div>
                    }

                    {props.document.metadata.isOldCopy && !props.document.metadata.annotationFinal &&
                        <div className={clsx(classes[imageSize].thumbnailIcon, classes[imageSize].thumbnailIconOldCopy)}/>
                    }

                    <img alt=""
                         className={clsx(classes[imageSize].thumbnailImage, props.thumbActive && classes[imageSize].thumbActive)}
                         loading="lazy"
                         onLoad={() => {
                             setLoading(false);
                         }}
                         onError={documentLoadError}
                         src={props.imageUrl}/>
                </div>
            </div>
            {!props.hideLabel &&
                <div>
                {props.position &&
                        <div className={classes[imageSize].thumbnailLabelCount}>
                            {props.position.current} / {props.position.count}
                        </div>
                    }
                    <div className={classes[imageSize].thumbnailLabel} onClick={() => {
                        if (props.onSelect && props.selectable) props.onSelect();
                    }}>
                        {/* TODO: allowing to select this for comparison .... maybe later ... ??? */}

                        {/*props.selected*/}
                        {props.selectable &&
                            <div className={classes[imageSize].selectBox}>
                                {!props.selected &&
                                    <UnselectedIcon className={classes[imageSize].selectBoxIcon} />
                                }
                                {props.selected &&
                                    <SelectedIcon className={classes[imageSize].selectBoxIcon} color="primary" />
                                }
                            </div>
                        }

                        {/* {props.document.startDate} */}

                        <div
                            className={classes[imageSize].labelProcedureName}>{Formatter.formatProcedureNameSimple(t,
                                props.document.metadata ? props.document.metadata.procedureName : undefined)}
                        </div>
                        {props.document.metadata && props.document.metadata.laterality &&
                            <span className={classes[imageSize].labelLaterality}>({Formatter.formatLaterality(props.document.metadata.laterality)})</span>}
                    </div>
                </div>
            }
        </div>
        </Tooltip>
    );
}

export default Thumbnail;
